import { Moment } from 'moment'
import React, { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { dateTime } from '@/configs'
import { FilterDate } from '@/pages/WashingCount'
import { defaultFilterDate } from '@/pages/WashingCount'
import { DateType } from '@/types'
import {
  ExportTotalWashingCountByRangeDayRequest,
  ExportWashingCountByDayRequest,
  ExportWashingCountByRangeDayRequest,
  GetTotalWashingCountByRangeDayRequest,
  GetWashingCountByDayRequest,
  GetWashingCountByRangeDayRequest,
} from '@/types/apis/washingCount'
import { formatDateSendRequest } from '@/utils/date'

import {
  ButtonCustom,
  ButtonWrapper,
  DatePickerCustom,
  Icon,
  RangeDateWrapper,
  SearchDayWrapper,
  SelectWrapper,
  TitleSearch,
  WrapperFilter,
} from '../styles'

type Props = {
  setDateFilter: React.Dispatch<React.SetStateAction<Moment | null | undefined>>
  getWashingCountByDay: (request: GetWashingCountByDayRequest) => Promise<void>
  exportExcelWashingCountByDay: (request: ExportWashingCountByDayRequest) => Promise<void>
  dateFilter?: Moment | null
  getWashingCountByRangeDay: (request: GetWashingCountByRangeDayRequest) => Promise<void>
  getTotalWashingCountByRangeDay: (request: GetTotalWashingCountByRangeDayRequest) => Promise<void>
  exportExcelWashingCountByRangeDay: (request: ExportWashingCountByRangeDayRequest) => Promise<void>
  exportExcelTotalWashingCountByRangeDay: (request: ExportTotalWashingCountByRangeDayRequest) => Promise<void>
  setDateRangeFilter: React.Dispatch<React.SetStateAction<FilterDate>>
  dateRangeFilter: FilterDate
  exportWashingCountLoading: boolean
  exportWashingCountByDayLoading: boolean
  exportWashingCountByRangeDayLoading: boolean
}

const FilterWashingCountByDay: FC<Props> = ({
  getWashingCountByDay,
  setDateFilter,
  dateFilter,
  dateRangeFilter,
  setDateRangeFilter,
  getWashingCountByRangeDay,
  getTotalWashingCountByRangeDay,
  exportExcelWashingCountByDay,
  exportExcelWashingCountByRangeDay,
  exportWashingCountLoading,
  exportWashingCountByDayLoading,
  exportWashingCountByRangeDayLoading,
  exportExcelTotalWashingCountByRangeDay,
}) => {
  const { t } = useTranslation()

  const handlePickByDay = (date: Moment | null) => {
    setDateRangeFilter((pre) => ({ ...pre, type: DateType.BY_DAY }))
    setDateFilter(date)
    getWashingCountByDay({ date: formatDateSendRequest(date) })
  }

  const handlePickRangeDay = (type: string, date: Moment | null) => {
    switch (type) {
      case 'fromByTotalDate':
        setDateRangeFilter((pre) => ({ ...pre, type: DateType.TOTAL_RANGE_DAY, totalFromDate: date }))
        date &&
          dateRangeFilter.totalToDate &&
          getTotalWashingCountByRangeDay({
            fromDate: formatDateSendRequest(date),
            toDate: formatDateSendRequest(dateRangeFilter.totalToDate),
          })
        break

      case 'toByTotalDate':
        setDateRangeFilter((pre) => ({
          ...pre,
          type: DateType.TOTAL_RANGE_DAY,
          totalToDate: date,
        }))
        getTotalWashingCountByRangeDay({
          fromDate: formatDateSendRequest(dateRangeFilter.totalFromDate),
          toDate: formatDateSendRequest(date),
        })
        break

      case 'fromByRangeDate':
        setDateRangeFilter((pre) => ({ ...pre, type: DateType.RANGE_DAY, fromDate: date }))
        date &&
          dateRangeFilter.toDate &&
          getWashingCountByRangeDay({
            fromDate: formatDateSendRequest(date),
            toDate: formatDateSendRequest(dateRangeFilter.toDate),
          })
        break

      case 'toByRangeDate':
        setDateRangeFilter((pre) => ({ ...pre, type: DateType.RANGE_DAY, toDate: date }))
        getWashingCountByRangeDay({
          fromDate: formatDateSendRequest(dateRangeFilter.fromDate),
          toDate: formatDateSendRequest(date),
        })
        break

      default:
        break
    }
  }

  const handleExportCSV = () => {
    if (dateFilter) {
      exportExcelWashingCountByDay({ date: formatDateSendRequest(dateFilter) })
    }
  }

  const handleExportCSVClientByRangeDay = () => {
    if (dateRangeFilter.fromDate && dateRangeFilter.toDate) {
      exportExcelWashingCountByRangeDay({
        fromDate: formatDateSendRequest(dateRangeFilter.fromDate),
        toDate: formatDateSendRequest(dateRangeFilter.toDate),
      })
    }
  }

  const handleExportCSVTotalByRangeDay = () => {
    if (dateRangeFilter.totalFromDate && dateRangeFilter.totalToDate) {
      exportExcelTotalWashingCountByRangeDay({
        fromDate: formatDateSendRequest(dateRangeFilter.totalFromDate),
        toDate: formatDateSendRequest(dateRangeFilter.totalToDate),
      })
    }
  }

  const handleDownLoadCSV = (type: DateType) => {
    switch (type) {
      case 1:
        handleExportCSV()
        break
      case 2:
        handleExportCSVClientByRangeDay()
        break
      case 3:
        handleExportCSVTotalByRangeDay()
        break
      default:
        break
    }
  }

  const handleReset = () => {
    setDateFilter(null)
    setDateRangeFilter(defaultFilterDate)
  }

  return (
    <WrapperFilter>
      <ButtonWrapper>
        <SearchDayWrapper>
          <TitleSearch>{t('components.pages.washingCount.listReport.filter.filterDay')}</TitleSearch>
          <SelectWrapper>
            <DatePickerCustom
              value={dateFilter}
              disabled={dateRangeFilter.type !== DateType.BY_DAY && dateRangeFilter.type !== DateType.NO_SELECT}
              format={dateTime.dateFormat}
              onChange={(date) => handlePickByDay(date)}
            />
          </SelectWrapper>
        </SearchDayWrapper>
      </ButtonWrapper>
      <ButtonWrapper>
        <SearchDayWrapper>
          <TitleSearch>{t('components.pages.washingCount.listReport.filter.filterByRangeDay')}</TitleSearch>
          <RangeDateWrapper>
            <DatePickerCustom
              value={dateRangeFilter.fromDate}
              onChange={(date) => handlePickRangeDay('fromByRangeDate', date)}
              format={dateTime.dateFormat}
              disabled={dateRangeFilter.type !== DateType.RANGE_DAY && dateRangeFilter.type !== DateType.NO_SELECT}
            />
            <Icon>~</Icon>
            <DatePickerCustom
              value={dateRangeFilter.toDate}
              onChange={(date) => handlePickRangeDay('toByRangeDate', date)}
              format={dateTime.dateFormat}
              disabled={dateRangeFilter.type !== DateType.RANGE_DAY && dateRangeFilter.type !== DateType.NO_SELECT}
            />
          </RangeDateWrapper>
        </SearchDayWrapper>
      </ButtonWrapper>
      <ButtonWrapper>
        <SearchDayWrapper>
          <TitleSearch>{t('components.pages.washingCount.listReport.filter.filterByTime')}</TitleSearch>
          <RangeDateWrapper>
            <DatePickerCustom
              value={dateRangeFilter.totalFromDate}
              onChange={(date) => handlePickRangeDay('fromByTotalDate', date)}
              format={dateTime.dateFormat}
              disabled={
                dateRangeFilter.type !== DateType.TOTAL_RANGE_DAY && dateRangeFilter.type !== DateType.NO_SELECT
              }
            />
            <Icon>~</Icon>
            <DatePickerCustom
              value={dateRangeFilter.totalToDate}
              onChange={(date) => handlePickRangeDay('toByTotalDate', date)}
              format={dateTime.dateFormat}
              disabled={
                dateRangeFilter.type !== DateType.TOTAL_RANGE_DAY && dateRangeFilter.type !== DateType.NO_SELECT
              }
            />
          </RangeDateWrapper>
        </SearchDayWrapper>
        {dateRangeFilter.type !== DateType.NO_SELECT && (
          <ButtonCustom danger onClick={handleReset}>
            {t('components.pages.washingCount.listReport.table.reset')}
          </ButtonCustom>
        )}
        <ButtonCustom
          marginLeft="30px"
          loading={exportWashingCountByDayLoading || exportWashingCountByRangeDayLoading || exportWashingCountLoading}
          onClick={() => handleDownLoadCSV(dateRangeFilter.type)}
        >
          {t('components.pages.washingCount.listReport.table.export')}
        </ButtonCustom>
      </ButtonWrapper>
    </WrapperFilter>
  )
}

FilterWashingCountByDay.displayName = 'FilterWashingCountByDay'
export default FilterWashingCountByDay
